import React, { useState } from "react";
import phoneFrame from "../assets/iphone.png";
import { getIsTablet } from "../globalConstants";


interface DismissibleBannerProps {
    text: string
    fontSize?: string | number
    bottom?: string | number
}

export const DismissibleBanner: React.FC<DismissibleBannerProps> = ({
    text,
    fontSize = "11px",
    bottom = "0%",
}) => {
    const [dismissed, setDismissed] = useState(false);

    if (dismissed) {
        return null;
    }

    return (
        <div style={{
            position: "absolute",
            bottom,
            left: "5%",
            right: "5%",
            background: "rgba(10, 10, 30, 0.45)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.35)",
            color: "rgba(255, 255, 255, 0.95)",
            textAlign: "center",
            fontSize,
            fontFamily: "Medium",
            padding: "5px 8px",
            zIndex: 10,
            letterSpacing: "0.25px",
            borderRadius: "8px",
            // Re-enable clicks even when an ancestor sets pointerEvents: "none"
            // (e.g. the hover-overlay cards in howItWorks), so the X stays clickable.
            pointerEvents: "auto",
        }}>
            {text}
            <div
                role="button"
                aria-label="Dismiss"
                onClick={(e) => {
                    e.stopPropagation();
                    setDismissed(true);
                }}
                style={{
                    position: "absolute",
                    top: "-9px",
                    right: "-9px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "rgba(110, 110, 120, 0.95)",
                    border: "1px solid rgba(255, 255, 255, 0.35)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "13px",
                    lineHeight: 1,
                    fontFamily: "Medium",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.35)",
                    zIndex: 11,
                }}
            >
                ×
            </div>
        </div>
    );
};


interface FlexiblePhoneProps {
    style?: React.CSSProperties
    videoBorderRadius?: string | number
    marginTop?: string | number
    marginBottom?: string | number
    marginHorizontal?: string | number
    video?: React.MediaHTMLAttributes<HTMLVideoElement>
    videoRef?: (ref: HTMLVideoElement | null) => void
    videoKey?: number
    banner?: string
    bannerFontSize?: string | number
}

export const FlexiblePhone: React.FC<FlexiblePhoneProps> = ({
    style = {
        width: "100%",
        height: "100%",
        position: "relative"
    },
    videoBorderRadius = 50,
    marginTop = "6%",
    marginBottom = "3%",
    marginHorizontal = "6%",
    video = null,
    videoRef,
    videoKey = 0,
    banner,
    bannerFontSize,
}) => {
    const resolvedBannerFontSize = bannerFontSize ?? (getIsTablet() ? "13px" : "11px");

    return (
        <div style={style}> {/* Add position: relative and overflow: hidden */}
            <div style={{ width: "100%", height: "100%", position: "absolute", zIndex: 2, }}>
                <img
                    src={phoneFrame}
                    style={{
                        width: "100%",  // Use width and height to fill the container
                        height: "100%",
                        objectFit: "fill",  // Keep the aspect ratio within the bounds
                        zIndex: 2,
                    }}
                    alt=""
                />
            </div>
            <div
                style={{
                    left: "2%",
                    width: "96%",
                    height: "100%",
                    position: "absolute",
                    display: "flex",  // Added display flex
                    justifyContent: "center",  // Center horizontally
                    borderRadius: videoBorderRadius,
                    overflow: "hidden",
                    zIndex: 1
                }}
            >
                <video
                    key={videoKey} // Force re-render when key changes
                    ref={videoRef} // Attach the ref
                    autoPlay
                    loop={false} // Keep loop as false
                    muted
                    playsInline
                    src={video}
                    style={{
                        flex: 1,
                        //  objectFit: "fill",  // Ensure the video fits properly
                        zIndex: -2,
                        marginTop: marginTop,
                        marginBottom: marginBottom,
                        marginLeft: marginHorizontal,
                        marginRight: marginHorizontal,
                    }}
                />
            </div>
            {banner && (
                <DismissibleBanner text={banner} fontSize={resolvedBannerFontSize} bottom="0%" />
            )}
            <div
                style={{
                    width: "96%",
                    height: "96%",
                    margin: "2%",
                    position: "absolute",
                    display: "flex",  // Added display flex
                    justifyContent: "center",  // Center horizontally
                    borderRadius: videoBorderRadius,
                    overflow: "hidden",
                    zIndex: 0,
                    backgroundColor: "black"
                }}
            >
            </div>
        </div>
    );
};

interface FlexiblePhoneStillImageProps {
    style?: React.CSSProperties
    videoBorderRadius?: string | number
    marginTop?: string | number
    marginBottom?: string | number
    marginHorizontal?: string | number
    image?: any
    imageKey?: number
    banner?: string
    bannerFontSize?: string | number
}


export const FlexiblePhoneStillImage: React.FC<FlexiblePhoneStillImageProps> = ({
    style = {
        width: "100%",
        height: "100%",
        position: "relative"
    },
    videoBorderRadius = 50,
    marginTop = "6%",
    marginBottom = "3%",
    marginHorizontal = "6%",
    image = null,
    imageKey = 0,
    banner,
    bannerFontSize,
}) => {
    const resolvedBannerFontSize = bannerFontSize ?? (getIsTablet() ? "15px" : "11px");

    return (
        <div style={style}> {/* Add position: relative and overflow: hidden */}
            <div style={{ width: "100%", height: "100%", position: "absolute", zIndex: 2, }}>
                <img
                    src={phoneFrame}
                    style={{
                        width: "100%",  // Use width and height to fill the container
                        height: "100%",
                        objectFit: "fill",  // Keep the aspect ratio within the bounds
                        zIndex: 2,
                    }}
                    alt=""
                />
            </div>
            <div
                style={{
                    left: "2%",
                    width: "96%",
                    height: "100%",
                    position: "absolute",
                    display: "flex",  // Added display flex
                    justifyContent: "center",  // Center horizontally
                    borderRadius: videoBorderRadius,
                    overflow: "hidden",
                    zIndex: 1
                }}
            >
                <img
                    key={imageKey} // Force re-render when key changes
                    alt=""
                    src={image}
                    style={{
                        flex: 1,
                        objectFit: "fill",  // Ensure the video fits properly
                        zIndex: -2,
                        marginTop: marginTop,
                        marginBottom: marginBottom,
                        marginLeft: marginHorizontal,
                        marginRight: marginHorizontal,
                    }}
                />
            </div>
            {banner && (
                <DismissibleBanner text={banner} fontSize={resolvedBannerFontSize} bottom="6%" />
            )}
            <div
                style={{
                    width: "96%",
                    height: "96%",
                    margin: "2%",
                    position: "absolute",
                    display: "flex",  // Added display flex
                    justifyContent: "center",  // Center horizontally
                    borderRadius: videoBorderRadius,
                    overflow: "hidden",
                    zIndex: 0,
                    backgroundColor: "black"
                }}
            >
            </div>
        </div>
    );
};

